import { css } from 'styled-components';

/**
 * The white sheet the phone orders list is drawn on.
 *
 * Here rather than on one of the three boxes that wear it — the list itself, the empty-state message,
 * and the skeleton's stand-in for the list — because all three have to agree. Written out separately
 * they drifted twice in one sitting: a change to the bleed or to the rules had to be made in each of
 * them, and a miss showed up as the placeholders sitting at a different width from the orders that
 * replaced them.
 *
 * Runs edge to edge: on a phone the page's own gutter costs more than it gives, so the sheet cancels
 * it and takes the full width, while the rows' own padding still keeps the text off the screen edge.
 * The negative margin mirrors the content padding in `layout/Main`, which is the same measure at every
 * width. Nothing guards it by breakpoint because `OrdersList` renders the phone list only below `sm` —
 * every box that wears this is a phone layout by construction.
 *
 * Square, and ruled above and below rather than boxed: a rounded corner sitting on the viewport edge
 * reads as clutter, so the two rules are what mark where the sheet starts and stops.
 */
export const listSheet = css`
  margin-inline: calc(-1 * ${({ theme }) => theme.spacing(3)});
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

/**
 * What bands one row off the next on that sheet: a rule between them and nowhere else, since one
 * above the first or below the last would double up with the sheet's own edge.
 *
 * Worn by the row's wrapper rather than by the row, so it spans the full sheet while the row's text
 * stays inset by its own padding — and shared for the same reason the sheet is, the skeleton drawing
 * its own bands out of `div`s where the real list uses `li`s.
 */
export const listBand = css`
  & + & {
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;
